import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, X } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { FilterDropdown } from '../components/common/FilterDropdown';
import { SortableTh } from '../components/common/SortableTh';
import { useTableSort, type SortAccessors } from '../utils/useTableSort';
import { organisationService } from '../services/organisationService';
import type { BvContact } from '../types/billValidationTypes';

type ContactRow = BvContact & { client_name?: string };

type ContactSortField = 'name' | 'client' | 'email' | 'phone' | 'role';

// Module-level so the sort memo isn't invalidated on every render.
const SORT_ACCESSORS: SortAccessors<ContactRow, ContactSortField> = {
  // Surname first: the alphabetical order people expect from a contact list.
  name:   (c) => `${c.last_name ?? ''} ${c.first_name ?? ''}`.trim(),
  client: (c) => c.client_name,
  email:  (c) => c.email,
  phone:  (c) => c.phone,
  role:   (c) => c.role,
};

export function ContactsListPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const result = await organisationService.listAllContacts();
    if (result.success) setContacts(result.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const clientOptions = useMemo(
    () => [...new Set(contacts.map((c) => c.client_name ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [contacts],
  );
  const roleOptions = useMemo(
    () => [...new Set(contacts.map((c) => c.role ?? '').filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [contacts],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter((c) => {
      const matchSearch = !q
        || `${c.first_name} ${c.last_name}`.toLowerCase().includes(q)
        || (c.email ?? '').toLowerCase().includes(q)
        || (c.phone ?? '').toLowerCase().includes(q)
        || (c.client_name ?? '').toLowerCase().includes(q)
        || (c.role ?? '').toLowerCase().includes(q);
      const matchClient = !clientFilter || c.client_name === clientFilter;
      const matchRole = !roleFilter || c.role === roleFilter;
      return matchSearch && matchClient && matchRole;
    });
  }, [contacts, search, clientFilter, roleFilter]);

  const { sortField, sortDir, onSort, sorted } = useTableSort(filtered, SORT_ACCESSORS, 'name');

  const hasActiveFilters = !!(search || clientFilter || roleFilter);
  const clearAllFilters = () => { setSearch(''); setClientFilter(''); setRoleFilter(''); };

  const th = (label: string, field: ContactSortField) => (
    <SortableTh
      label={label}
      field={field}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      className="px-6"
    />
  );

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Contacts</h1>
          <p className="text-sm text-gray-500 mt-0.5">All contacts across your entities</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-8 h-9 w-52 text-sm"
            placeholder="Search name, email or entity…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <FilterDropdown label="Entity" value={clientFilter} onChange={setClientFilter} options={clientOptions} searchable />
        <FilterDropdown label="Role" value={roleFilter} onChange={setRoleFilter} options={roleOptions} />
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 text-xs text-gray-500 gap-1" onClick={clearAllFilters}>
            <X className="h-3.5 w-3.5" />
            Clear filters
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex-1">
        {loading ? (
          <div className="space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <Users className="h-12 w-12 mb-3 opacity-20" />
            <p className="text-sm font-medium">
              {hasActiveFilters ? 'No contacts match your search' : 'No contacts yet'}
            </p>
            {!hasActiveFilters && (
              <p className="text-xs mt-1 text-gray-400">
                Add contacts via an entity's detail page
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100">
                  {th('Name', 'name')}
                  {th('Entity', 'client')}
                  {th('Email', 'email')}
                  {th('Phone', 'phone')}
                  {th('Role', 'role')}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sorted.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/bill-validation/contacts/${c.id}`)}
                    className="hover:bg-blue-50/30 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {c.first_name} {c.last_name}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {c.client_name ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">{c.email ?? '—'}</td>
                    <td className="px-6 py-4 text-muted-foreground">{c.phone ?? '—'}</td>
                    <td className="px-6 py-4 text-muted-foreground">{c.role ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="px-1 text-xs text-gray-500">
        {loading
          ? '…'
          : `${sorted.length} contact${sorted.length === 1 ? '' : 's'}${
              hasActiveFilters ? ` of ${contacts.length}` : ''
            }`}
      </div>
    </div>
  );
}
